import { AppConfig } from '@/constants/AppConfig';
import { AuthApi } from '@/utilities/api/AuthApi';
import { DetailedApiError } from '@/utilities/api/DetailedApiError';
import { RetailerApi } from '@/utilities/api/RetailerApi';
import { UserApi } from '@/utilities/api/UserApi';
import { LocalAccountList } from '@/utilities/LocalAccountList';
import { UserEndpoint } from '@api/endpoints';
import { ApiRetailer } from '@api/interfaces';
import { ApiUser } from '@api/interfaces/ApiUser';
import {
  isInternalUser,
  isRetailerAdminUser,
  isRetailerUser,
  isValidPhoneNumber,
} from '@shared/utilities';
import React, { ReactNode, useContext, useEffect, useState } from 'react';

/** `users/me` answers 403 with the Cognito person when their login doesn't list LP. */
const noLpAccessOf = (
  error: unknown,
): UserEndpoint.Me.NoLpAccessDetails['cognitoUser'] | undefined => {
  if (!(error instanceof DetailedApiError) || error.code !== '403') {
    return undefined;
  }
  const details = (error.details as { details?: Partial<UserEndpoint.Me.NoLpAccessDetails> })
    .details;
  return details?.cognitoUser;
};

export enum SessionState {
  UNKNOWN,
  LOGGED_OUT,
  LOGGED_IN,
  /** Signed in to the shared login, but LP isn't one of its products. */
  NO_LP_ACCESS,
}

interface LogoutOptions {
  keepCognitoSession?: boolean,
  removeFromAccountSwitcher?: boolean,
}

export interface IAuthenticationContext {
  authenticate: (
    email: string,
    password: string,
  ) => Promise<void>,
  isInternal: boolean,
  isRetailer: boolean,
  isRetailerAdmin: boolean,
  logout: (options?: LogoutOptions) => Promise<void>,
  platformLinks: UserEndpoint.PlatformLinks.Response,
  refreshUser: () => Promise<ApiUser | undefined>,
  retailer: ApiRetailer | undefined,
  sessionState: SessionState,
  updateUser: (userData: Partial<UserEndpoint.Update.Request>) => Promise<ApiUser | undefined>,
  user?: ApiUser,
  validateOtp: (
    username: string,
    code: string,
  ) => Promise<void>,
}

export const AuthenticationContext = React.createContext<IAuthenticationContext>(
  {} as IAuthenticationContext,
);

const { Provider } = AuthenticationContext;

export const AuthenticationProvider = (props: { children: ReactNode }) => {
  const [user, setUser] = useState<ApiUser>();
  const [retailer, setRetailer] = useState<ApiRetailer>();
  const [isInternal, setIsInternal] = useState(false);
  const [isRetailer, setIsRetailer] = useState(false);
  const [isRetailerAdmin, setIsRetailerAdmin] = useState(false);
  const [sessionState, setSessionState] = useState(SessionState.UNKNOWN);
  const [platformLinks, setPlatformLinks] = useState<UserEndpoint.PlatformLinks.Response>([]);

  const clearUserContext = () => {
    setIsRetailer(false);
    setIsInternal(false);
    setUser(undefined);
    setRetailer(undefined);
    setPlatformLinks([]);
    setSessionState(SessionState.LOGGED_OUT);
  };

  const updateUserContext = async ({ cognitoUser, ...user }: UserEndpoint.Me.Response) => {
    setUser(user);
    setPlatformLinks(cognitoUser?.allowedPlatformLinks ?? []);
    setIsInternal(isInternalUser(user));
    if (isRetailerUser(user) && user.retailerId) {
      const retailer = await RetailerApi.get(user.retailerId);
      setIsRetailer(true);
      setRetailer(retailer);
    } else {
      setIsRetailer(false);
      setRetailer(undefined);
    }
    setIsRetailerAdmin(isRetailerAdminUser(user));
    await LocalAccountList.remember(user);
    setSessionState(SessionState.LOGGED_IN);
  };

  const checkLoggedInStatus = async () => {
    try {
      const user = await UserApi.me();
      await updateUserContext(user);
      return user;
    } catch (error) {
      const cognitoUser = noLpAccessOf(error);
      if (cognitoUser) {
        setPlatformLinks(cognitoUser.allowedPlatformLinks);
        setSessionState(SessionState.NO_LP_ACCESS);
        return;
      }
      if (error instanceof DetailedApiError && error.code === '401') {
        await AuthApi.logout();
      }
      clearUserContext();
    }
  };

  useEffect(() => {
    void checkLoggedInStatus();
  }, []);

  // The sign-in answers with the plain user; /users/me adds the links to the other platforms.
  const authenticate = async (email: string, password: string) => {
    await AuthApi.login({ email, password });
    await updateUserContext(await UserApi.me());
  };

  const updateUser = async (userData: UserEndpoint.Update.Request) => {
    if (!user?.id) {
      return;
    }
    const updatedUser = await UserApi.update(user.id, {
      ...userData,
      postal: userData.postal || undefined,
    });
    setUser(updatedUser);
    return updatedUser;
  };

  const logout = async (options: LogoutOptions = {}) => {
    const {
      keepCognitoSession = false,
      removeFromAccountSwitcher = true,
    } = options;
    if (AppConfig.env.test && removeFromAccountSwitcher) {
      LocalAccountList.remove(user!.id);
    }
    clearUserContext();
    await AuthApi.logout({ keepCognitoSession });
  };

  const validateOtp = async (
    username: string,
    code: string,
  ) => {
    const isTelephone = isValidPhoneNumber(username.replace(/[() -]/g, ""), AppConfig.env.test);
    if (!isTelephone) {
      throw new Error('OTP validation only supports phone numbers');
    }
    
    const validateOtpRequest: UserEndpoint.ValidateOtp.Request = {
      code,
      telephone: username.replace(/[() -]/g, ""),
    };
    
    const user = await AuthApi.validateOtp(validateOtpRequest);
    if (user) {
      await updateUserContext(await UserApi.me());
    } else {
      throw new Error('Error logging in');
    }
  };

  if (sessionState === SessionState.UNKNOWN ||
    (sessionState === SessionState.LOGGED_IN && isRetailer && !retailer)
  ) {
    return null;
  }

  return (
    <Provider value={{
      authenticate,
      isInternal,
      isRetailer,
      isRetailerAdmin,
      logout,
      platformLinks,
      refreshUser: checkLoggedInStatus,
      retailer,
      sessionState,
      updateUser,
      user,
      validateOtp,
    }}
    >
      {props.children}
    </Provider>
  );
};

export const useAuthentication = () => {
  const context = useContext(AuthenticationContext);
  if (!context) {
    throw new Error('useAuthentication must be used within an AuthenticationProvider');
  }
  return context as IAuthenticationContext;
};
